import React, { useState, useEffect, useRef } from 'react';
import CustomerServicePopup from './CustomerServicePopup';
import { useUserProfile } from '../hooks/useUserProfile';
import { useAuth } from '../hooks/useAuth';
import { doc, onSnapshot, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

function DepositPage({ onBack }) {
  const { user } = useAuth();
  const { userProfile, refreshProfile } = useUserProfile(user?.uid);
  const pageRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('deposit');
  const [showCustomerServicePopup, setShowCustomerServicePopup] = useState(false);

  // Scroll to top function
  const scrollToTop = () => {
    // Try multiple methods to ensure it works
    if (window.scrollTo) {
      window.scrollTo(0, 0);
    }
    if (window.scroll) {
      window.scroll(0, 0);
    }
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
    // Try scrolling the page container if it exists
    if (pageRef.current) {
      pageRef.current.scrollTop = 0;
    }
  };

  // Scroll to top when deposit page opens
  useEffect(() => {
    // Scroll immediately
    scrollToTop();
    
    // Scroll after multiple delays to handle async rendering
    const timeout1 = setTimeout(scrollToTop, 10);
    const timeout2 = setTimeout(scrollToTop, 100);
    const timeout3 = setTimeout(scrollToTop, 300);
    const timeout4 = setTimeout(scrollToTop, 500);
    
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
    };
  }, []);

  // Scroll to top when tab changes
  useEffect(() => {
    scrollToTop();
    const timeout = setTimeout(scrollToTop, 100);
    return () => clearTimeout(timeout);
  }, [activeTab]);

  // Real-time listener for user profile to detect knife removal
  useEffect(() => {
    if (!user?.uid) return;
    
    // Track initial knife status to only navigate back when knife is removed (not when it was never applied)
    let initialKnifeStatus = null;
    let hasInitialized = false;
    
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = { id: docSnapshot.id, ...docSnapshot.data() };
        const knifeStatus = userData.hasNegativeBalance || (userData.balances?.negative && userData.balances.negative < 0);
        
        // Initialize the initial knife status on first load
        if (!hasInitialized) {
          initialKnifeStatus = knifeStatus;
          hasInitialized = true;
          return; // Don't navigate on initial load
        }
        
        // Only navigate back if knife was previously applied and is now removed
        if (initialKnifeStatus === true && !knifeStatus && onBack) {
          console.log('🔓 Knife removed - refreshing profile');
          refreshProfile();
          // Small delay to ensure profile is updated
          setTimeout(() => {
            onBack();
          }, 500);
        }
        
        // Update the initial status for subsequent checks
        initialKnifeStatus = knifeStatus;
      }
    }, (error) => {
      console.error('Error in user profile listener:', error);
    });
    
    return () => unsubscribe();
  }, [user?.uid, onBack, refreshProfile]);

  // Refresh profile when deposit page loads
  useEffect(() => {
    if (!user?.uid) return;
    refreshProfile();
  }, [user?.uid, refreshProfile]);

  const handleContactService = () => {
    setShowCustomerServicePopup(true);
  };

  // Get user balance
  const walletBalance = userProfile?.balances?.wallet || 0;
  const frozenBalance = userProfile?.balances?.frozen || 0;
  const totalBalance = userProfile?.balances?.total || 0;
  const negativeAmount = userProfile?.balances?.negativeAmount || (userProfile?.balances?.negative || 0);
  
  // Check if knife is applied
  const hasKnifeApplied = userProfile?.hasNegativeBalance || userProfile?.isKnifeApplied || false;
  
  // Check if knife was removed and user needs to submit
  const knifeRemovedPendingSubmit = userProfile?.knifeRemovedPendingSubmit || false;
  
  // When knife is applied, show negative amount needing deposit
  const displayWalletBalance = hasKnifeApplied && walletBalance < 0 ? walletBalance : walletBalance;
  const displayFrozenBalance = frozenBalance;
  const displayTotalBalance = hasKnifeApplied && totalBalance < 0 ? totalBalance : totalBalance;
  
  // Amount needing deposit (positive number representing the deficit)
  const depositNeeded = hasKnifeApplied && negativeAmount > 0 ? negativeAmount : 0;
  
  // Handle submit after knife removal
  const handleSubmitAfterKnifeRemoval = async () => {
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        knifeRemovedPendingSubmit: false,
        updatedAt: Timestamp.now()
      });
      
      // Refresh profile
      refreshProfile();
      
      // Show success message
      alert('✅ Optimization tasks enabled! You can now start tasks.');
      
      // Navigate back after a short delay
      setTimeout(() => {
        if (onBack) onBack();
      }, 1000);
    } catch (error) {
      console.error('Error submitting after knife removal:', error);
      alert('Error enabling optimization. Please try again.');
    }
  };

  return (
    <div className="deposit-page" ref={pageRef}>
      {/* Top Navigation Bar */}
      <div className="deposit-top-bar">
        <div className="deposit-back-btn" onClick={onBack}>
          <i className="bi bi-chevron-left"></i>
        </div>
        <div className="deposit-title-text">Deposit</div>
        <div className="deposit-spacer"></div>
      </div>

      {/* Tabbed Interface */}
      <div className="deposit-tabs">
        <div 
          className={`deposit-tab ${activeTab === 'deposit' ? 'active' : ''}`}
          onClick={() => setActiveTab('deposit')}
        >
          Deposit
        </div>
        <div 
          className={`deposit-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </div>
      </div>

      {/* Main Content */}
      <div className="deposit-content">
        {activeTab === 'deposit' ? (
          <>
            {/* Submit Button After Knife Removal */}
            {knifeRemovedPendingSubmit && !hasKnifeApplied && (
              <div style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                borderRadius: '15px',
                padding: '25px',
                marginBottom: '20px',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
              }}>
                <div style={{ color: 'white', textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: '600', 
                    marginBottom: '15px'
                  }}>
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Knife Removed Successfully!
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    opacity: 0.9,
                    marginBottom: '20px'
                  }}>
                    Your account has been restored. Click the button below to enable optimization tasks.
                  </div>
                  <button
                    onClick={handleSubmitAfterKnifeRemoval}
                    style={{
                      background: 'white',
                      color: '#059669',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px 30px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      width: '100%',
                      maxWidth: '300px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Submit & Enable Optimization
                  </button>
                </div>
              </div>
            )}

            {/* Account Amount Banner - Special UI when knife is applied */}
            {hasKnifeApplied ? (
              <div style={{
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                borderRadius: '15px',
                padding: '25px',
                marginBottom: '20px',
                boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
              }}>
                <div style={{ color: 'white' }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    marginBottom: '15px',
                    opacity: 0.9
                  }}>
                    Account Balance
                  </div>
                  <div style={{ 
                    fontSize: '32px', 
                    fontWeight: 'bold', 
                    color: 'white',
                    marginBottom: '10px'
                  }}>
                    {displayTotalBalance.toFixed(2)} USDT
                  </div>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: '600',
                    marginBottom: '15px',
                    color: '#fef2f2',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px'
                  }}>
                    Deposit ${depositNeeded.toFixed(2)} to continue
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    opacity: 0.9,
                    color: 'white',
                    marginTop: '10px'
                  }}>
                    <div>Frozen Balance: {displayFrozenBalance.toFixed(2)} USDT</div>
                    <div style={{ marginTop: '5px' }}>Wallet: {displayWalletBalance.toFixed(2)} USDT</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="deposit-amount-banner">
                <div className="deposit-amount-info">
                  <div className="deposit-amount-label">Current Balance</div>
                  <div className="deposit-amount-value">{totalBalance.toFixed(2)} USDT</div>
                  <div className="deposit-amount-subtitle">Wallet: {walletBalance.toFixed(2)} USDT</div>
                </div>
              </div>
            )}

            {/* Deposit Instructions - Hide when knife is applied */}
            {!hasKnifeApplied && (
            <div className="deposit-instructions">
              <div className="deposit-instructions-header">
                <i className="bi bi-info-circle me-2"></i>
                <h3>How to Deposit</h3>
              </div>
              <div className="deposit-instructions-content">
                <div className="deposit-step">
                  <div className="deposit-step-number">1</div>
                  <div className="deposit-step-content">
                    <h4>Contact Customer Service</h4>
                    <p>Click the button below to contact our customer service team. They will provide you with the merchant's payment account details.</p>
                  </div>
                </div>
                <div className="deposit-step">
                  <div className="deposit-step-number">2</div>
                  <div className="deposit-step-content">
                    <h4>Confirm Payment Details</h4>
                    <p>Verify the merchant's digital wallet address and deposit amount with customer service before making the transfer.</p>
                  </div>
                </div>
                <div className="deposit-step">
                  <div className="deposit-step-number">3</div>
                  <div className="deposit-step-content">
                    <h4>Submit Transfer Receipt</h4>
                    <p>After completing the transfer, provide the transaction receipt to customer service for processing.</p>
                  </div>
                </div>
                <div className="deposit-step">
                  <div className="deposit-step-number">4</div>
                  <div className="deposit-step-content">
                    <h4>Funds Added</h4>
                    <p>Once verified, your deposit will be processed and funds will be added to your account balance.</p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Important Notice - Hide when knife is applied */}
            {!hasKnifeApplied && (
            <div className="deposit-notice">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <div>
                <strong>Important:</strong> Always confirm the payment account details with customer service before making any deposit. The platform is not responsible for deposits made to incorrect addresses.
              </div>
            </div>
            )}
            
            {/* Contact Customer Service Button - Special styling when knife is applied */}
            {hasKnifeApplied ? (
              <button 
                onClick={handleContactService}
                style={{
                  background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                  border: 'none',
                  borderRadius: '15px',
                  padding: '18px 25px',
                  width: '100%',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 15px rgba(168, 85, 247, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(168, 85, 247, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(168, 85, 247, 0.3)';
                }}
              >
                <i className="bi bi-headphones" style={{ marginRight: '10px', fontSize: '20px' }}></i>
                Contact Customer Service
              </button>
            ) : (
              <button 
                className="deposit-contact-btn"
                onClick={handleContactService}
              >
                <i className="bi bi-headset me-2"></i>
                Contact Customer Service
              </button>
            )}
          </>
        ) : (
          <div className="deposit-history-content">
            <div className="deposit-history-header">
              <h3>Deposit History</h3>
              <p className="text-muted">Your deposit transaction history will appear here</p>
            </div>
            <div className="deposit-history-empty">
              <i className="bi bi-clock-history"></i>
              <p>No deposit history available</p>
              <small>Completed deposits will be displayed here</small>
            </div>
          </div>
        )}
      </div>
      
      {/* Customer Service Popup */}
      {showCustomerServicePopup && (
        <CustomerServicePopup onClose={() => setShowCustomerServicePopup(false)} />
      )}
    </div>
  );
}

export default DepositPage;

